/**
 * ProposedEnhancementsDialog — Fullscreen ownership-deck view of every
 * Resort Area that carries at least one "enhancement" (vs. plain
 * "repair") OR has an `enhancements_roi` block.
 *
 * The dialog is intentionally beautiful and marketing-grade — it's the
 * surface that converts ownership/investor walk-throughs into capex
 * approvals. Each card shows the area name, its description, the
 * specific enhancement items (the ones that "glow red" in the Areas
 * CMS), and the full ROI block (scope / investment / return / timeline
 * / benefits / narrative). A media gallery rotates through renders →
 * photos → videos in that order so the imagined future is what the
 * decision-maker sees first.
 *
 * Data source: GET /api/areas (already public). We filter client-side
 * for areas that have at least one `enhancement.kind === 'enhancement'`
 * OR a non-empty `enhancements_roi`. No backend changes required.
 *
 * Closing: Esc, click-out, or the explicit Close pill in the header.
 * Print: re-uses /lib/print.js to render a deck-ready A4 export.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Printer, Play, Image as ImageIcon, FileText, Search,
  TrendingUp, Clock, DollarSign, Target, Sparkles, ChevronRight, Maximize2, Pen,
} from 'lucide-react';
import { printHtml } from '../lib/print';
import { useLightbox } from './LightboxProvider';
import DeckCardEditor from './DeckCardEditor';
import { displayMoney } from '../lib/money';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function abs(u) {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND}${u}`;
}

function isVideo(url) {
  return /\.(mp4|webm|mov|m4v|ogv)$/i.test(url || '');
}

function hasCrucialEnhancement(area) {
  const hasMarkedEnh = (area.enhancements || []).some((e) => e.kind === 'enhancement');
  const hasRoi = (area.enhancements_roi || '').trim().length > 0
    || (area.roi_investment || '').trim().length > 0
    || (area.roi_return || '').trim().length > 0;
  return hasMarkedEnh || hasRoi;
}

/**
 * Split a list of URLs into videos vs. images. Used so the slider can
 * always show concept VIDEOS first, then concept IMAGES, then actual
 * site photos — that's the order ownership decks should follow (sell
 * the future before showing the rubble).
 */
function partitionMedia(urls, label) {
  const out = [];
  (urls || []).forEach((u) => {
    out.push({
      url: u.startsWith?.('http') ? u : `${BACKEND}${u}`,
      kind: isVideo(u) ? 'video' : 'image',
      label,
    });
  });
  return out;
}

function videoMimeForUrl(url) {
  const u = (url || '').toLowerCase();
  if (u.endsWith('.webm')) return 'video/webm';
  if (u.endsWith('.ogv')) return 'video/ogg';
  if (u.endsWith('.m4v')) return 'video/x-m4v';
  // Default — `.mov` is reported as video/mp4 by the backend
  // (`_MIME_MAP` patched in iter-72) so Chrome attempts playback. Most
  // iPhone .mov uploads are H.264/AAC and decode fine.
  return 'video/mp4';
}

/* ─────────── Fullscreen video viewer ───────────
   Click-to-enlarge for videos. Renders above the deck (z-index 10001)
   with native HTML5 controls so ownership can scrub through the full
   render without leaving the dialog. */
function VideoFullscreenViewer({ src, onClose }) {
  // Esc to close
  useEffect(() => {
    if (!src) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, onClose]);
  if (!src) return null;
  return (
    <div
      data-testid="enh-video-fullscreen"
      className="fixed inset-0 flex items-center justify-center p-4 sm:p-8"
      style={{ background: 'rgba(0,0,0,0.95)', zIndex: 10001 }}
      onClick={onClose}
    >
      <button
        data-testid="enh-video-fullscreen-close"
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close video"
        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg ring-1 ring-black/10 hover:bg-red-50 hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
        style={{ touchAction: 'manipulation' }}
      >
        <X className="w-6 h-6" strokeWidth={2.5} />
      </button>
      <video
        key={src}
        controls
        autoPlay
        playsInline
        preload="auto"
        className="max-w-full max-h-full rounded-lg bg-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <source src={src} type={videoMimeForUrl(src)} />
        <source src={src} type="video/quicktime" />
      </video>
    </div>
  );
}

/* ─────────── Media slider ─────────── */
function MediaSlider({ items, areaName }) {
  const [idx, setIdx] = useState(0);
  const [fsVideo, setFsVideo] = useState(null);
  const { open: openLightbox } = useLightbox();

  // All image URLs in this slider — fed to the lightbox so admins can
  // navigate prev/next through every photo of this card without
  // leaving the lightbox.
  const imageUrls = (items || []).filter((it) => it.kind === 'image').map((it) => it.url);

  if (!items || items.length === 0) {
    return (
      <div
        className="w-full aspect-[16/10] rounded-2xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #EAE5D9 0%, #F4F1EB 100%)' }}
      >
        <ImageIcon className="w-10 h-10 opacity-40" style={{ color: '#7A8C8C' }} />
      </div>
    );
  }
  const cur = items[idx % items.length];
  return (
    <div className="relative">
      <div
        className="w-full aspect-[16/10] rounded-2xl overflow-hidden flex items-center justify-center"
        style={{ background: '#0A1A2F' }}
      >
        {cur.kind === 'video' ? (
          <>
            <video
              // `key` forces React to remount the <video> when the user
              // switches between videos in the thumbnail strip — without
              // it the underlying media element keeps the previous src in
              // its buffer and ignores the new <source>.
              key={cur.url}
              controls
              playsInline
              preload="metadata"
              poster={cur.poster || undefined}
              className="w-full h-full object-contain bg-black"
            >
              <source src={cur.url} type={videoMimeForUrl(cur.url)} />
              {/* Belt-and-suspenders: also list raw quicktime in case the
                  backend mime override is bypassed for some reason. */}
              <source src={cur.url} type="video/quicktime" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>
                <Play className="w-8 h-8 mb-2 opacity-60" />
                <p className="text-xs font-bold mb-1">This video format isn&apos;t supported in your browser.</p>
                <a href={cur.url} target="_blank" rel="noreferrer" className="text-[11px] underline">
                  Download to view ↗
                </a>
              </div>
            </video>
            {/* Enlarge button — sits over the video, top-right. Clicks
                stop propagation so the native controls bar stays
                responsive. */}
            <button
              type="button"
              data-testid={`enh-video-enlarge-${idx}`}
              onClick={(e) => { e.stopPropagation(); setFsVideo(cur.url); }}
              aria-label="Enlarge video"
              title="Enlarge"
              className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold transition-transform hover:scale-105 active:scale-95 cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.95)', color: '#0A1A2F', fontFamily: 'Manrope', touchAction: 'manipulation' }}
            >
              <Maximize2 className="w-3 h-3" strokeWidth={2.5} />
              Enlarge
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => openLightbox(cur.url, imageUrls)}
            aria-label={`Enlarge ${cur.label}`}
            data-testid={`enh-image-enlarge-${idx}`}
            className="w-full h-full p-0 m-0 cursor-zoom-in"
            style={{ touchAction: 'manipulation' }}
          >
            <img
              src={cur.url}
              alt={`${areaName} ${cur.label}`}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-[1.02]"
              loading="lazy"
            />
          </button>
        )}
        <span
          className="absolute top-3 left-3 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider pointer-events-none"
          style={{ background: 'rgba(10,26,47,0.85)', color: '#A3D4D4', fontFamily: 'Manrope' }}
        >
          {cur.kind === 'video' ? <><Play className="w-3 h-3 inline mr-1" /> Concept video</>
            : cur.label === 'Render' ? <><Sparkles className="w-3 h-3 inline mr-1" /> Concept render</>
              : <><ImageIcon className="w-3 h-3 inline mr-1" /> {cur.label}</>}
        </span>
      </div>
      {items.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              className="w-12 h-12 rounded-md overflow-hidden border-2 transition-all"
              style={{
                borderColor: i === idx ? '#1F4FBE' : 'transparent',
                opacity: i === idx ? 1 : 0.55,
              }}
            >
              {it.kind === 'video' ? (
                <div className="w-full h-full flex items-center justify-center" style={{ background: '#0A1A2F' }}>
                  <Play className="w-4 h-4" style={{ color: '#A3D4D4' }} />
                </div>
              ) : (
                <img src={it.url} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
      <VideoFullscreenViewer src={fsVideo} onClose={() => setFsVideo(null)} />
    </div>
  );
}

/* ─────────── Single enhancement card ─────────── */
function EnhancementCard({ area, canEdit, onEdit }) {
  const enhItems = (area.enhancements || []).filter((e) => e.kind === 'enhancement');
  const repairItems = (area.enhancements || []).filter((e) => e.kind !== 'enhancement');

  // Media priority (iter-71): RENDERED VIDEOS / animations first, then
  // rendered images, then actual site photos / site videos. Sells the
  // FUTURE before showing the rubble. `renders` may contain both video
  // and image files; we split each list into video/image buckets so we
  // can interleave by priority instead of by source array.
  const renderMedia = partitionMedia(area.renders, 'Render');
  const renderVideos = renderMedia.filter((m) => m.kind === 'video');
  const renderImages = renderMedia.filter((m) => m.kind === 'image');

  // Dedicated `videos` field added in iter-71 — treat as "concept
  // videos" so they sit right behind the render videos in priority.
  const conceptVideos = partitionMedia(area.videos, 'Concept video').filter((m) => m.kind === 'video');

  // Real site photos / videos last.
  const siteMedia = partitionMedia(area.images, 'Photo');
  const sitePhotos = siteMedia.filter((m) => m.kind === 'image');
  const siteVideos = siteMedia.filter((m) => m.kind === 'video');

  const media = [
    ...renderVideos,    // 1. RENDERED VIDEOS / animations
    ...renderImages,    // 2. RENDERED IMAGES
    ...conceptVideos,   // 3. Concept videos (videos[] field)
    ...sitePhotos,      // 4. Actual site photos
    ...siteVideos,      // 5. Actual site videos
  ];

  return (
    <article
      data-testid={`enh-card-${area.id}`}
      className="rounded-3xl overflow-hidden grid lg:grid-cols-[1.15fr_1fr] gap-0"
      style={{ background: 'white', boxShadow: '0 6px 36px -10px rgba(10, 26, 47, 0.18)' }}
    >
      {/* Media column */}
      <div className="p-5 sm:p-6 lg:p-7" style={{ background: '#FAF8F3' }}>
        <MediaSlider items={media} areaName={area.name} />
      </div>

      {/* Content column */}
      <div className="p-6 sm:p-8 lg:p-10 flex flex-col relative">
        {/* iter-110 — Admin / engineer quick-edit widget. Hidden for
            viewers. Opens the DeckCardEditor side sheet with Manual +
            AI prompt tabs. */}
        {canEdit && (
          <button
            type="button"
            onClick={() => onEdit?.(area)}
            data-testid={`deck-card-edit-${area.id}`}
            title="Quick edit · manual or with AI"
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #1F4FBE 0%, #3B82F6 100%)',
              color: 'white',
              boxShadow: '0 4px 14px rgba(31,79,190,0.35)',
            }}
          >
            <Pen className="w-3.5 h-3.5" />
          </button>
        )}
        <div className="flex items-start justify-between gap-3 mb-1">
          <p
            className="text-[10px] uppercase tracking-[0.22em] font-bold"
            style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}
          >
            {area.zone || 'Resort'} · {area.completion?.replace('-', ' ') || 'Proposed'}
          </p>
          {area.progress_percent > 0 && (
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: '#DCFCE7', color: '#15803D', fontFamily: 'Manrope' }}
            >
              {area.progress_percent}% complete
            </span>
          )}
        </div>
        <h3
          className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif", color: '#0A1A2F', fontWeight: 500 }}
          data-testid={`enh-card-title-${area.id}`}
        >
          {area.name}
        </h3>
        {area.description && (
          <p
            className="text-sm leading-relaxed mb-5"
            style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
          >
            {area.description}
          </p>
        )}

        {/* Enhancements list (red-glow kind) */}
        {enhItems.length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#B45309', fontFamily: 'Manrope' }}>
              <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" /> Proposed Enhancements ({enhItems.length})
            </p>
            <ul className="space-y-2">
              {enhItems.map((e, i) => (
                <li
                  key={i}
                  className="px-3 py-2 rounded-lg text-xs"
                  style={{
                    background: 'linear-gradient(135deg, #FEF3C7 0%, #FFFBEB 100%)',
                    border: '1px solid #FDE68A',
                  }}
                >
                  <p className="font-bold" style={{ color: '#92400E', fontFamily: 'Manrope' }}>{e.title}</p>
                  {e.description && (
                    <p className="mt-0.5" style={{ color: '#7A4F00', fontFamily: 'Manrope' }}>{e.description}</p>
                  )}
                  {(e.progress_percent || 0) > 0 && (
                    <p className="text-[10px] mt-1" style={{ color: '#7A4F00' }}>{e.progress_percent}% complete</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ROI block */}
        {(area.enhancements_roi || area.roi_investment || area.roi_return || area.roi_timeline) && (
          <div className="mb-5 rounded-2xl p-4" style={{ background: '#F0F4FA', border: '1px solid #C7DBF7' }}>
            <p className="text-[10px] uppercase tracking-wider font-bold mb-3" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
              <TrendingUp className="w-3 h-3 inline mr-1 -mt-0.5" /> ROI Analysis
            </p>
            <div className="grid grid-cols-2 gap-3">
              {area.roi_investment && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                    <DollarSign className="w-2.5 h-2.5 inline" /> Investment
                  </p>
                  <p className="text-sm font-bold mt-0.5" style={{ color: '#0A1A2F' }}>{displayMoney(area.roi_investment)}</p>
                </div>
              )}
              {area.roi_return && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                    <TrendingUp className="w-2.5 h-2.5 inline" /> Return
                  </p>
                  <p className="text-sm font-bold mt-0.5" style={{ color: '#15803D' }}>{displayMoney(area.roi_return)}</p>
                </div>
              )}
              {area.roi_timeline && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                    <Clock className="w-2.5 h-2.5 inline" /> Timeline
                  </p>
                  <p className="text-sm font-bold mt-0.5" style={{ color: '#0A1A2F' }}>{area.roi_timeline}</p>
                </div>
              )}
              {area.roi_scope && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                    <Target className="w-2.5 h-2.5 inline" /> Scope
                  </p>
                  <p className="text-sm font-bold mt-0.5" style={{ color: '#0A1A2F' }}>{area.roi_scope}</p>
                </div>
              )}
            </div>
            {area.roi_benefits && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: '#C7DBF7' }}>
                <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                  Strategic benefits
                </p>
                <p className="text-xs leading-relaxed" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{area.roi_benefits}</p>
              </div>
            )}
            {area.enhancements_roi && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: '#C7DBF7' }}>
                <p className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                  Narrative
                </p>
                <p className="text-xs whitespace-pre-wrap leading-relaxed" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{area.enhancements_roi}</p>
              </div>
            )}
          </div>
        )}

        {/* Repairs (smaller) */}
        {repairItems.length > 0 && (
          <details className="mb-3">
            <summary className="text-[10px] uppercase tracking-wider font-bold cursor-pointer" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              + {repairItems.length} restoration item{repairItems.length === 1 ? '' : 's'} (included in scope)
            </summary>
            <ul className="mt-2 space-y-1 pl-3 list-disc text-xs" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              {repairItems.map((r, i) => (
                <li key={i}>{r.title}{r.description ? ` — ${r.description}` : ''}</li>
              ))}
            </ul>
          </details>
        )}

        {(area.documents || []).length > 0 && (
          <div className="mt-auto pt-3 flex flex-wrap gap-2 text-[10px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
            <FileText className="w-3 h-3" />
            {area.documents.length} attached document{area.documents.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </article>
  );
}

/* ─────────── Main dialog ─────────── */
export default function ProposedEnhancementsDialog({ open, onClose, areas }) {
  // Curation rule (iter-71):
  // 1. If ANY area has `promote_to_deck=true` → show ONLY those (manual
  //    curation mode). Admins use this to pick the exact 22 projects
  //    shown to ownership.
  // 2. Otherwise → auto-pull every area that has at least one
  //    enhancement OR ROI data (safety-net so the deck is never empty
  //    when admins forget to flag).
  // Sort order (iter-79):
  // 1. `deck_order` (explicit drag-and-drop order from admin Deck
  //    Builder) — lower index renders first.
  // 2. Areas without `deck_order` set fall back to `position_x` then
  //    name so legacy data still has a deterministic order.
  const enhancementAreas = useMemo(() => {
    const list = areas || [];
    const promoted = list.filter((a) => a.promote_to_deck === true);
    const base = promoted.length > 0 ? promoted : list.filter(hasCrucialEnhancement);
    return [...base].sort((a, b) => {
      const ao = (a.deck_order === null || a.deck_order === undefined) ? Number.POSITIVE_INFINITY : Number(a.deck_order);
      const bo = (b.deck_order === null || b.deck_order === undefined) ? Number.POSITIVE_INFINITY : Number(b.deck_order);
      if (ao !== bo) return ao - bo;
      const ax = Number(a.position_x ?? 50);
      const bx = Number(b.position_x ?? 50);
      if (ax !== bx) return ax - bx;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [areas]);
  const totalInvestment = enhancementAreas.length;
  const isCuratedMode = (areas || []).some((a) => a.promote_to_deck === true);

  // iter-129 — Public search box for the deck. Filters the already-curated
  // `enhancementAreas` list by name / description / zone / individual
  // enhancement text so investors and execs can jump straight to the
  // project they want to discuss without scrolling 22 cards.
  const [searchQuery, setSearchQuery] = useState('');
  const visibleAreas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return enhancementAreas;
    return enhancementAreas.filter((a) => {
      const hay = [
        a.name,
        a.description,
        a.zone,
        a.enhancements_roi,
        a.roi_investment,
        a.roi_return,
        a.roi_timeline,
        a.roi_benefits,
        ...(a.enhancements || []).map((e) => e?.text || ''),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [enhancementAreas, searchQuery]);

  // Track a "closing" state so the X click flashes pointer-events:none +
  // opacity 0 INSTANTLY, even before React tears down the dialog. This
  // is the difference between "the X did something" and "the X is
  // broken" — perception fix for the iter-72 close-button complaint.
  const [closing, setClosing] = useState(false);
  // iter-110 — Quick-edit state. canEdit is gated by role from the
  // local token cache; viewers see no pencil button. editingArea is the
  // currently-open card's area object (null = sheet closed).
  const role = (typeof window !== 'undefined' && (localStorage.getItem('user_role') || '')) || '';
  const canEdit = role === 'admin' || role === 'engineer' || role === 'editor';
  const [editingArea, setEditingArea] = useState(null);
  // Local override map keyed by area.id so an edit instantly reflects in
  // the deck without waiting for the parent to refetch /api/areas.
  const [localOverrides, setLocalOverrides] = useState({});
  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    // 150ms is enough for the fade-out to register visually but short
    // enough that it never feels like a stall.
    setTimeout(() => { setClosing(false); onClose?.(); }, 150);
  };

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handlePrint = () => {
    const cards = enhancementAreas.map((a) => `
      <article style="border-top:6px solid #1F4FBE;padding:18mm 0 12mm;margin-bottom:14mm;page-break-inside:avoid">
        <p style="font-size:9pt;letter-spacing:0.18em;color:#1F4FBE;font-weight:bold;text-transform:uppercase;margin:0 0 4mm">${a.zone || 'Resort'} · ${a.completion || 'Proposed'}</p>
        <h1 style="font-family:'Cormorant Garamond',serif;font-size:32pt;color:#0A1A2F;margin:0 0 6mm;font-weight:500">${a.name}</h1>
        <p style="font-size:11pt;color:#1A2E2E;line-height:1.55;margin:0 0 8mm">${a.description || ''}</p>
        ${a.roi_investment ? `<p><b>Investment:</b> ${displayMoney(a.roi_investment)}</p>` : ''}
        ${a.roi_return ? `<p><b>Return:</b> ${displayMoney(a.roi_return)}</p>` : ''}
        ${a.roi_timeline ? `<p><b>Timeline:</b> ${a.roi_timeline}</p>` : ''}
        ${a.roi_scope ? `<p><b>Scope:</b> ${a.roi_scope}</p>` : ''}
        ${a.roi_benefits ? `<p style="margin-top:4mm"><b>Benefits:</b> ${a.roi_benefits}</p>` : ''}
        ${a.enhancements_roi ? `<p style="margin-top:4mm;white-space:pre-wrap">${a.enhancements_roi}</p>` : ''}
      </article>
    `).join('');
    printHtml(cards, 'Proposed Crucial Enhancements');
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col transition-opacity duration-150"
      style={{
        background: 'linear-gradient(180deg, #FAF8F3 0%, #F4F1EB 100%)',
        opacity: closing ? 0 : 1,
        pointerEvents: closing ? 'none' : 'auto',
      }}
      data-testid="proposed-enhancements-dialog"
    >
      {/* Header bar — sticky */}
      <header
        className="flex-shrink-0 border-b backdrop-blur-md sticky top-0 z-10"
        style={{ background: 'rgba(10, 26, 47, 0.96)', borderColor: '#1F4FBE' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p
              className="text-[10px] uppercase tracking-[0.25em] font-bold mb-0.5"
              style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}
            >
              Ownership Opportunity
            </p>
            <h1
              className="text-xl sm:text-2xl md:text-3xl italic leading-tight truncate"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: 'white' }}
            >
              Proposed Crucial Enhancements
              <span className="ml-2 text-base sm:text-lg" style={{ color: '#A3D4D4' }}>
                · {totalInvestment} project{totalInvestment === 1 ? '' : 's'}
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <AiEnhanceDeckButton areas={enhancementAreas} />
            <button
              type="button"
              onClick={handlePrint}
              data-testid="enh-print-deck"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
              style={{ background: 'white', color: '#1F4FBE', fontFamily: 'Manrope' }}
            >
              <Printer className="w-3.5 h-3.5" /> Print deck
            </button>
            <button
              type="button"
              onClick={handleClose}
              data-testid="enh-close"
              className="inline-flex items-center justify-center w-12 h-12 rounded-full transition-all hover:scale-110 active:scale-90 hover:bg-red-50"
              style={{ background: 'white', color: '#0A1A2F' }}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          {/* Lede */}
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <p
              className="text-sm sm:text-base italic leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: '#4A5D5D' }}
            >
              A pivotal moment to build forward. Each project below is a
              curated, ROI-led upgrade — engineered to lift{' '}
              <strong style={{ color: '#1F4FBE' }}>RevPAR</strong>, sharpen
              brand positioning, and elevate the guest experience above
              pre-hurricane benchmarks. Mobilised crews are already
              on-site; approving now compounds the value of every dollar
              already in motion. Open any card for the concept, the
              numbers, and the ownership case.
            </p>
            {isCuratedMode && (
              <p
                className="text-[10px] uppercase tracking-[0.22em] font-bold mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
                style={{ background: '#FEF3C7', color: '#92400E', fontFamily: 'Manrope' }}
                data-testid="enh-curated-badge"
              >
                <Sparkles className="w-3 h-3" /> Curated by admin
              </p>
            )}
          </div>

          {/* iter-129 — Search box: filters the deck client-side. Hidden
              when the deck is empty so we don't show controls with
              nothing to act on. */}
          {enhancementAreas.length > 0 && (
            <div className="mb-8 max-w-2xl mx-auto">
              <div
                className="relative flex items-center"
                style={{ fontFamily: 'Manrope' }}
              >
                <Search
                  className="absolute left-4 w-4 h-4 pointer-events-none"
                  style={{ color: '#7A8C8C' }}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="enh-search-input"
                  placeholder="Search projects by name, zone, scope, or ROI…"
                  className="w-full pl-11 pr-10 py-3 text-sm rounded-full focus:outline-none"
                  style={{
                    background: '#F4F8F8',
                    color: '#0A1A2F',
                    border: '1px solid #DCE5E5',
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    data-testid="enh-search-clear"
                    className="absolute right-3 w-6 h-6 rounded-full flex items-center justify-center transition-colors hover:bg-slate-200"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" style={{ color: '#4A5D5D' }} />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p
                  className="text-[11px] text-center mt-2"
                  style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
                  data-testid="enh-search-result-count"
                >
                  {visibleAreas.length} of {enhancementAreas.length} project
                  {enhancementAreas.length === 1 ? '' : 's'} match
                  {visibleAreas.length === 1 ? 'es' : ''} “{searchQuery}”
                </p>
              )}
            </div>
          )}

          {enhancementAreas.length === 0 ? (
            <div className="text-center py-16">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: '#A3D4D4' }} />
              <p className="text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                No enhancement projects have been published yet. Admins can
                mark any Area item as an "enhancement" (red glow) in the
                Admin → Areas panel to surface it here.
              </p>
            </div>
          ) : visibleAreas.length === 0 ? (
            <div className="text-center py-16" data-testid="enh-search-empty">
              <Search className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: '#A3D4D4' }} />
              <p className="text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                No projects match “{searchQuery}”. Try a shorter or different keyword.
              </p>
            </div>
          ) : (
            <div className="space-y-8 sm:space-y-10">
              {visibleAreas.map((area) => {
                const merged = localOverrides[area.id] ? { ...area, ...localOverrides[area.id] } : area;
                return (
                  <EnhancementCard
                    key={area.id}
                    area={merged}
                    canEdit={canEdit}
                    onEdit={(a) => setEditingArea(a)}
                  />
                );
              })}
            </div>
          )}

          {/* Closing CTA */}
          {enhancementAreas.length > 0 && (
            <div className="mt-12 text-center">
              <p
                className="text-xs italic"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#7A8C8C' }}
              >
                {enhancementAreas.length} projects · Combined upside outlined
                above
              </p>
              <button
                type="button"
                onClick={handleClose}
                data-testid="enh-close-bottom"
                className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold text-white transition-transform hover:-translate-y-0.5 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #003DA5 100%)', fontFamily: 'Manrope' }}
              >
                Back to the map <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* iter-110 — Quick-edit side sheet for admin/engineer/editor.
          Saving emits the updated area which we cache in localOverrides
          so the deck re-renders without a parent refetch. */}
      <DeckCardEditor
        open={!!editingArea}
        area={editingArea || {}}
        onClose={() => setEditingArea(null)}
        onSaved={(updated) => {
          if (updated?.id) setLocalOverrides((o) => ({ ...o, [updated.id]: updated }));
        }}
      />
    </div>
  );
}

/* iter-80 — AI-polish the entire ownership deck into a single
   investor-ready narrative. Pulls every enhancement area's ROI block,
   ships it to /api/admin/ai/polish-document with `doc_type=report` and
   audience=ownership, and opens the resulting markdown in a new tab.
   Admin-only — viewer/editor see no button. Visible to admins on the
   deck header next to Print so they can generate a board pack in 1
   click from the same surface they review the deck on. */
function AiEnhanceDeckButton({ areas }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [busy, setBusy] = useState(false);
  if (!adminToken) return null;
  const run = async () => {
    if (busy) return;
    if (!areas || areas.length === 0) {
      toast.error('No enhancement areas to polish yet.');
      return;
    }
    const { getAiProviderArgs } = await import('../lib/aiProviderPicker');
    const providerArgs = await getAiProviderArgs();
    if (providerArgs === null) return;
    setBusy(true);
    toast.message('Drafting an ownership-grade narrative — 20–40s.', { duration: 6000 });
    try {
      const raw = [
        '# Proposed Crucial Enhancements — Source Material',
        '',
        ...areas.map((a, i) => [
          `## ${i + 1}. ${a.name}${a.zone ? ` · ${a.zone}` : ''}`,
          a.description || '',
          a.roi_scope ? `Scope: ${a.roi_scope}` : '',
          a.roi_investment ? `Investment: ${displayMoney(a.roi_investment)}` : '',
          a.roi_return ? `Return: ${displayMoney(a.roi_return)}` : '',
          a.roi_timeline ? `Timeline: ${a.roi_timeline}` : '',
          a.roi_benefits ? `Benefits: ${a.roi_benefits}` : '',
          a.enhancements_roi ? `Notes: ${a.enhancements_roi}` : '',
          '',
        ].filter(Boolean).join('\n')),
      ].join('\n');
      const { data } = await axios.post(`${API}/admin/ai/polish-document`, {
        raw_text: raw.slice(0, 18000),
        doc_type: 'report',
        audience: 'ownership',
        keep_original: false,
        ...providerArgs,
      }, { headers: { Authorization: `Bearer ${adminToken}` } });
      const w = window.open('', '_blank');
      if (w) {
        const safe = (s) => (s || '').replace(/[<>&]/g, (m) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[m]));
        w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${safe(data.title || 'Atrium — Crucial Enhancements')}</title><style>body{font-family:Manrope,system-ui;max-width:820px;margin:40px auto;padding:0 24px;color:#0A1A2F;line-height:1.6}h1{color:#003DA5;border-bottom:2px solid #003DA5;padding-bottom:8px}h2{color:#1F4FBE;margin-top:32px}blockquote{border-left:4px solid #003DA5;padding-left:16px;color:#4A5D5D;font-style:italic;background:#F8F5EC;padding:12px 16px;border-radius:4px}pre{white-space:pre-wrap;background:#F8F5EC;padding:18px;border-radius:8px;font-family:ui-monospace,Menlo,monospace;font-size:13px;line-height:1.55}</style></head><body>`);
        w.document.write(`<pre>${safe(data.polished_markdown || '')}</pre>`);
        w.document.write('</body></html>');
        w.document.close();
      }
      toast.success('Ownership narrative opened in a new tab.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI enhance failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid="enh-ai-enhance-deck"
      title="Generate a board-ready narrative across every project with AI"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
      style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', boxShadow: '0 4px 12px rgba(139,92,246,0.45)', fontFamily: 'Manrope' }}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {busy ? 'Drafting…' : 'Enhance with AI'}
    </button>
  );
}
